import { useMemo, useCallback, useRef } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { TextField } from "@mui/material";
import "./table.css";

const Table = ({
  columns,
  data,
  onRowClick,
  onPageChange,
  onPageSizeChange,
  totalRows,
  perPage,
  currentPage,
  sorting = [],
  onSortingChange,
  columnFilters = [],
  onColumnFiltersChange,
  columnFilterFns = {},
  onColumnFilterFnsChange,
}) => {
  const shouldUseManualFiltering = Boolean(onColumnFiltersChange);
  
  const transformedColumns = useMemo(() => {
    return columns.map((col) => {
      const isDateColumn =
        col.filterVariant === "date" ||
        /data/i.test(col.field || "") ||
        /data/i.test(col.header || "");

      const columnFilterModeOptions =
        col.columnFilterModeOptions ||
        (isDateColumn
          ? ["equals", "notEquals", "between", "greaterThan", "greaterThanOrEqualTo", "lessThan", "lessThanOrEqualTo"] // for dates: only equality by default
          : [
              "contains",
              "equals",
              "notEquals",
            ]);

      // Find current filter value for this column
      const currentFilter = Array.isArray(columnFilters)
        ? columnFilters.find((f) => f.id === col.field)
        : null;
      const currentFilterValue = currentFilter?.value || "";

      // For date columns, use a custom filter component to fully control the input
      const Filter = isDateColumn
        ? ({ column, table }) => {
            const filterValue = column.getFilterValue();
            const filterFn = column.getFilterFn();
            // Check both the filterFn and columnFilterFns to determine if it's "between" mode
            const currentFilterFn = columnFilterFns?.[col.field] || filterFn;
            // Get the actual filter mode from the column's filterFn or columnFilterFns
            // Material React Table uses different names, so check both
            const filterFnName = typeof filterFn === 'function' ? filterFn.name : filterFn;
            const isBetween = 
              currentFilterFn === "between" || 
              currentFilterFn === "betweenInclusive" ||
              filterFnName === "between" || 
              filterFnName === "betweenInclusive" ||
              (Array.isArray(filterValue) && filterValue.length === 2); // Only if value is explicitly a 2-element array
            
            // If no filter value exists, explicitly set to empty string to prevent browser default
            const hasNoFilter = filterValue === undefined || filterValue === null || 
              (typeof filterValue === "string" && filterValue.trim() === "") ||
              (Array.isArray(filterValue) && filterValue.every(v => !v || (typeof v === "string" && v.trim() === "")));
            
            if (isBetween) {
              // For "between" mode, show two date inputs
              // Explicitly set to empty strings if no filter value to prevent browser showing today's date
              const dateRange = hasNoFilter
                ? ["", ""]
                : Array.isArray(filterValue) 
                ? filterValue 
                : typeof filterValue === "string" && filterValue.includes(",")
                ? filterValue.split(",")
                : ["", ""];
              
              const startDate = dateRange[0] && typeof dateRange[0] === "string" && dateRange[0].trim()
                ? dateRange[0].split("T")[0] 
                : "";
              const endDate = dateRange[1] && typeof dateRange[1] === "string" && dateRange[1].trim()
                ? dateRange[1].split("T")[0] 
                : "";

              return (
                <div 
                  key={`${col.field}-between-filter`} 
                  id={`${col.field}-filter`}
                  style={{ display: "flex", gap: "8px", alignItems: "center" }} 
                  data-custom-filter="true"
                  data-filter-field={col.field}
                >
                  <TextField
                    key={`${col.field}-start-date`}
                    type="date"
                    value={startDate || ""}
                    defaultValue=""
                    onChange={(e) => {
                      const newStart = e.target.value;
                      // Get the current end date from the column's filter value to avoid stale closure
                      const currentFilterValue = column.getFilterValue();
                      const currentRange = Array.isArray(currentFilterValue) 
                        ? currentFilterValue 
                        : typeof currentFilterValue === "string" && currentFilterValue.includes(",")
                        ? currentFilterValue.split(",")
                        : ["", ""];
                      const currentEnd = currentRange[1] && typeof currentRange[1] === "string" && currentRange[1].trim()
                        ? currentRange[1].split("T")[0]
                        : "";
                      
                      const newRange = [newStart || null, currentEnd || null];
                      // Always set as array in "between" mode to maintain the mode state
                      column.setFilterValue(newRange);
                      // Update our filter state
                      if (onColumnFiltersChange) {
                        const currentFilters = Array.isArray(columnFilters) ? columnFilters : [];
                        const filtered = currentFilters.filter((f) => f.id !== col.field);
                        // Always include the filter in "between" mode, even if both dates are empty
                        const newFilters = [
                          ...filtered,
                          {
                            id: col.field,
                            value: newRange,
                            filterFn: "between",
                          },
                        ];
                        onColumnFiltersChange(newFilters);
                      }
                    }}
                    placeholder=""
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: "150px" }}
                    inputProps={{ 
                      placeholder: "",
                      style: { color: startDate ? 'inherit' : 'transparent' }
                    }}
                  />
                  <span style={{ color: "#6b7280" }}>a</span>
                  <TextField
                    key={`${col.field}-end-date`}
                    type="date"
                    value={endDate || ""}
                    defaultValue=""
                    onChange={(e) => {
                      const newEnd = e.target.value;
                      // Get the current start date from the column's filter value to avoid stale closure
                      const currentFilterValue = column.getFilterValue();
                      const currentRange = Array.isArray(currentFilterValue) 
                        ? currentFilterValue 
                        : typeof currentFilterValue === "string" && currentFilterValue.includes(",")
                        ? currentFilterValue.split(",")
                        : ["", ""];
                      const currentStart = currentRange[0] && typeof currentRange[0] === "string" && currentRange[0].trim()
                        ? currentRange[0].split("T")[0]
                        : "";
                      
                      const newRange = [currentStart || null, newEnd || null];
                      // Always set as array in "between" mode to maintain the mode state
                      column.setFilterValue(newRange);
                      // Update our filter state
                      if (onColumnFiltersChange) {
                        const currentFilters = Array.isArray(columnFilters) ? columnFilters : [];
                        const filtered = currentFilters.filter((f) => f.id !== col.field);
                        // Always include the filter in "between" mode, even if both dates are empty
                        const newFilters = [
                          ...filtered,
                          {
                            id: col.field,
                            value: newRange,
                            filterFn: "between",
                          },
                        ];
                        onColumnFiltersChange(newFilters);
                      }
                    }}
                    placeholder=""
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: "150px" }}
                    inputProps={{ 
                      placeholder: "",
                      style: { color: endDate ? 'inherit' : 'transparent' }
                    }}
                  />
                </div>
              );
            } else {
              // For other modes, show single date input
              // Explicitly set to empty string if no filter value to prevent browser showing today's date
              const dateValue = hasNoFilter 
                ? "" 
                : filterValue && typeof filterValue === "string" && filterValue.trim()
                ? filterValue.split("T")[0]
                : "";

              return (
                <div id={`${col.field}-filter`} data-custom-filter="true">
                  <TextField
                    type="date"
                    value={dateValue || ""}
                    defaultValue=""
                    onChange={(e) => {
                    const newValue = e.target.value;
                    // Only set filter value if a date is actually selected
                    if (newValue) {
                      column.setFilterValue(newValue);
                    } else {
                      column.setFilterValue(undefined);
                    }
                    // Also update our filter state
                    if (onColumnFiltersChange) {
                      const currentFilters = Array.isArray(columnFilters) ? columnFilters : [];
                      const filtered = currentFilters.filter((f) => f.id !== col.field);
                      if (newValue) {
                        const newFilters = [
                          ...filtered,
                          {
                            id: col.field,
                            value: newValue,
                          },
                        ];
                        onColumnFiltersChange(newFilters);
                      } else {
                        onColumnFiltersChange(filtered);
                      }
                    }
                  }}
                  placeholder=""
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: "150px" }}
                  inputProps={{ 
                    placeholder: "",
                    style: { color: dateValue ? 'inherit' : 'transparent' }
                  }}
                />
                </div>
              );
            }
          }
        : undefined;

      const muiFilterTextFieldProps = isDateColumn
        ? undefined // Don't use muiFilterTextFieldProps for date columns, use custom Filter instead
        : col.muiFilterTextFieldProps;

      if (col.Cell) {
        return {
          header: col.header,
          Cell: col.Cell,
          size: col.size || 150,
          columnFilterModeOptions,
          muiFilterTextFieldProps,
          defaultFilterFn: isDateColumn && !shouldUseManualFiltering ? "equals" : isDateColumn ? undefined : "contains",
        };
      }

      if (col.field?.includes(".")) {
        return {
          accessorFn: (row) =>
            col.field.split(".").reduce((obj, key) => obj?.[key], row),
          id: col.field,
          header: col.header,
          size: col.size || 150,
          enableColumnFilter: true,
          enableColumnFilterModes: true,
          columnFilterModeOptions,
          muiFilterTextFieldProps,
          Filter,
          defaultFilterFn: isDateColumn && !shouldUseManualFiltering ? "equals" : isDateColumn ? undefined : "contains",
          // Set a filter function that prevents auto-removal for date columns ONLY when doing backend filtering
          // When doing frontend filtering (no manual filtering), let MaterialReactTable handle it automatically
          filterFn: isDateColumn && shouldUseManualFiltering
            ? (row, columnId, filterValue) => {
                // Always return true to prevent auto-removal
                // The actual filtering is done on the backend
                return true;
              }
            : undefined,
        };
      }

      return {
        accessorKey: col.field,
        header: col.header,
        size: col.size || 150,
        enableColumnFilter: true,
        enableColumnFilterModes: true,
        columnFilterModeOptions,
        muiFilterTextFieldProps,
        Filter,
        defaultFilterFn: isDateColumn && !shouldUseManualFiltering ? "equals" : isDateColumn ? undefined : "contains",
        // Set a filter function that prevents auto-removal for date columns ONLY when doing backend filtering
        // When doing frontend filtering (no manual filtering), let MaterialReactTable handle it automatically
        filterFn: isDateColumn && shouldUseManualFiltering
          ? (row, columnId, filterValue) => {
              // Always return true to prevent auto-removal
              // The actual filtering is done on the backend
              return true;
            }
          : undefined,
      };
    });
  }, [columns, columnFilters, columnFilterFns, onColumnFiltersChange, shouldUseManualFiltering]);

  const shouldUseManualPagination = Boolean(onPageChange);
  const shouldUseManualSorting = Boolean(onSortingChange);
  const shouldUseManualFilterFns =
    shouldUseManualFiltering && Boolean(onColumnFilterFnsChange);

  const getControlledPagination = () => ({
    pageIndex: Math.max((currentPage ?? 1) - 1, 0),
    pageSize: perPage ?? 10,
  });

  const handlePaginationChange = (updater) => {
    if (!shouldUseManualPagination) return;

    const previous = getControlledPagination();
    const next =
      typeof updater === "function" ? updater(previous) : updater ?? previous;

    if (
      onPageSizeChange &&
      typeof next?.pageSize === "number" &&
      next.pageSize !== previous.pageSize
    ) {
      onPageSizeChange(next.pageSize);
    }

    if (
      onPageChange &&
      typeof next?.pageIndex === "number" &&
      next.pageIndex !== previous.pageIndex
    ) {
      onPageChange(next.pageIndex + 1);
    } else if (
      onPageChange &&
      typeof next?.pageSize === "number" &&
      next.pageSize !== previous.pageSize &&
      typeof next?.pageIndex === "number" &&
      next.pageIndex === previous.pageIndex
    ) {
      onPageChange(1);
    }
  };

  const handleSortingChange = (updater) => {
    if (!shouldUseManualSorting || !onSortingChange) return;

    const previousSorting = Array.isArray(sorting) ? sorting : [];
    const nextSorting =
      typeof updater === "function"
        ? updater(previousSorting)
        : updater ?? previousSorting;

    onSortingChange(nextSorting);
  };

  const handleColumnFiltersChange = useCallback((updater) => {
    if (!shouldUseManualFiltering || !onColumnFiltersChange) return;

    const previousFilters = Array.isArray(columnFilters) ? columnFilters : [];
    const nextFilters =
      typeof updater === "function" ? updater(previousFilters) : updater ?? previousFilters;

    // Auto-set default filter function to "contains" for non-date columns
    if (shouldUseManualFilterFns && onColumnFilterFnsChange) {
      const updatedFilterFns = { ...columnFilterFns };
      let hasChanges = false;

      nextFilters.forEach((filter) => {
        if (!filter?.id) return;

        // Check if this is a date column
        const col = columns.find((c) => c.field === filter.id);
        const isDateColumn =
          col?.filterVariant === "date" ||
          /data/i.test(col?.field || "") ||
          /data/i.test(col?.header || "");

        // If filter function is not set and it's not a date column, default to "contains"
        if (!updatedFilterFns[filter.id] && !isDateColumn) {
          updatedFilterFns[filter.id] = "contains";
          hasChanges = true;
        }
      });

      // Remove filter functions for filters that no longer exist
      Object.keys(updatedFilterFns).forEach((filterId) => {
        if (!nextFilters.find((f) => f.id === filterId)) {
          delete updatedFilterFns[filterId];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        onColumnFilterFnsChange(updatedFilterFns);
      }
    }

    onColumnFiltersChange(nextFilters);
  }, [shouldUseManualFiltering, shouldUseManualFilterFns, onColumnFiltersChange, onColumnFilterFnsChange, columnFilters, columnFilterFns, columns]);

  const tableState = {};
  if (shouldUseManualPagination) {
    tableState.pagination = getControlledPagination();
  }

  if (shouldUseManualSorting) {
    tableState.sorting = Array.isArray(sorting) ? sorting : [];
  }

  if (shouldUseManualFiltering) {
    // Ensure date filter values are preserved and properly formatted
    const preservedFilters = Array.isArray(columnFilters)
      ? columnFilters.map((filter) => {
          // Ensure date values are strings in YYYY-MM-DD format
          if (filter?.value && typeof filter.value === "string" && /^\d{4}-\d{2}-\d{2}/.test(filter.value)) {
            return {
              ...filter,
              value: filter.value.split("T")[0], // Ensure it's just the date part
            };
          }
          return filter;
        })
      : [];
    tableState.columnFilters = preservedFilters;
  }

  if (shouldUseManualFilterFns) {
    tableState.columnFilterFns = columnFilterFns || {};
  }

  const tableConfig = {
    columns: transformedColumns,
    data: data || [],
    manualPagination: shouldUseManualPagination,
    manualSorting: shouldUseManualSorting,
    manualFiltering: shouldUseManualFiltering,
    ...(shouldUseManualPagination && {
      onPaginationChange: handlePaginationChange,
    }),
    ...(shouldUseManualSorting && {
      onSortingChange: handleSortingChange,
    }),
    ...(shouldUseManualFiltering && {
      onColumnFiltersChange: handleColumnFiltersChange,
    }),
    ...(shouldUseManualFilterFns && {
      onColumnFilterFnsChange: onColumnFilterFnsChange,
    }),
    ...(shouldUseManualPagination && {
      rowCount: totalRows ?? data?.length ?? 0,
    }),
    state: tableState,
    enableColumnFilterModes: true,
    // Prevent auto-removal of filters (especially for dates)
    autoResetAll: false,
    // Enable pagination (will be automatic when manualPagination is false)
    enablePagination: true,

    initialState: {
      showColumnFilters: true,
      // Set default pagination state for frontend pagination
      ...(shouldUseManualPagination ? {} : {
        pagination: {
          pageIndex: 0,
          pageSize: 20,
        },
      }),
    },
    enableGlobalFilter: true,
    enableColumnActions: true,
    enableDensityToggle: true,
    enableFullScreenToggle: true,
    // enableColumnResizing: true,
    columnResizeMode: "onChange",

    // Italian localization
    localization: {
      actions: "Azioni",
      and: "e",
      rowsPerPage: "Righe per pagina",
      labelDisplayedRows: ({ from, to, count }) => {
        console.log("labelDisplayedRows called");
        return `${from}–${to} di ${count !== -1 ? count : `più di ${to}`}`;
      },
      cancel: "Annulla",
      changeFilterMode: "Cambia modalità filtro",
      changeSearchMode: "Cambia modalità ricerca",
      clearFilter: "Cancella filtro",
      clearSearch: "Cancella ricerca",
      clearSort: "Cancella ordinamento",
      clickToCopy: "Clicca per copiare",
      collapse: "Comprimi",
      collapseAll: "Comprimi tutto",
      columnActions: "Azioni colonna",
      copiedToClipboard: "Copiato negli appunti",
      dropToGroupBy: "Rilascia per raggruppare per {column}",
      edit: "Modifica",
      expand: "Espandi",
      expandAll: "Espandi tutto",
      filterByColumn: "Filtra per {column}",
      filterMode: "Modalità filtro",
      groupedBy: "Raggruppato per ",
      hideAll: "Nascondi tutto",
      hideColumn: "Nascondi colonna {column}",
      rowActions: "Azioni riga",
      save: "Salva",
      search: "Cerca",
      selectedCountOfRowCountRowsSelected:
        "{selectedCount} di {rowCount} riga(e) selezionata(e)",
      showAll: "Mostra tutto",
      showHideColumns: "Mostra/Nascondi colonne",
      showHideFilters: "Mostra/Nascondi filtri",
      showHideSearch: "Mostra/Nascondi ricerca",
      sortByColumn: "Ordina per {column}",
      thenBy: "poi per",
      toggleDensity: "Cambia densità",
      toggleFullScreen: "Schermo intero",
      ungroupByColumn: "Rimuovi raggruppamento per {column}",

      // Filter mode translations
      fuzzy: "Approssimativo",
      contains: "Contiene",
      startsWith: "Inizia con",
      endsWith: "Finisce con",
      equals: "Uguale a",
      notEquals: "Diverso da",
      between: "Tra",
      betweenInclusive: "Tra (inclusivo)",
      empty: "Vuoto",
      notEmpty: "Non vuoto",
      isNull: "È nullo",
      isNotNull: "Non è nullo",
      greaterThan: "Maggiore di",
      greaterThanOrEqualTo: "Maggiore o uguale a",
      lessThan: "Minore di",
      lessThanOrEqualTo: "Minore o uguale a",
      "aria-label": {
        actions: "Azioni",
        clearFilter: "Cancella filtro",
        clearSearch: "Cancella ricerca",
        clearSort: "Cancella ordinamento",
        columnActions: "Azioni colonna",
        dropToGroupBy: "Rilascia per raggruppare per {column}",
        edit: "Modifica",
        expand: "Espandi",
        filterByColumn: "Filtra per {column}",
        hideColumn: "Nascondi colonna {column}",
        rowActions: "Azioni riga",
        save: "Salva",
        search: "Cerca",
        showHideColumns: "Mostra/Nascondi colonne",
        showHideFilters: "Mostra/Nascondi filtri",
        showHideSearch: "Mostra/Nascondi ricerca",
        sortByColumn: "Ordina per {column}",
        toggleDensity: "Cambia densità",
        toggleFullScreen: "Schermo intero",
        ungroupByColumn: "Rimuovi raggruppamento per {column}",
      },
    },

    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        overflow: "hidden",
        border: "none",
        boxShadow: "none",
        background: "transparent",
      },
    },

    muiTableContainerProps: {
      sx: {
        borderRadius: "0",
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: "4px",
          "&:hover": {
            background: "#a8a8a8",
          },
        },
      },
    },

    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F4F6F8", // soft solid light grey
        color: "#2b3240",
        fontSize: "16px",
        fontWeight: "700",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        borderBottom: "2px solid #E0E3E7",
        padding: "18px 14px",
        textShadow: "0 1px 2px rgba(0,0,0,0.04)",
        boxShadow: "0 1px 4px rgba(60,72,88,0.04)",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        "& .MuiInputBase-root": {
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          borderRadius: "8px",
          color: "#2b3240",
          "& input": {
            color: "#2b3240",
            "::placeholder": {
              color: "rgba(107,114,128,0.8)",
            },
          },
        },
        "& .MuiFormHelperText-root": {
          color: "rgba(55,65,81,0.9)",
          fontWeight: "500",
        },
        "& svg": {
          color: "#2b3240 !important",
        },
        "& span > span > svg": {
          color: "#2b3240 !important",
        },
        "& .MuiIconButton-root": {
          color: "#2b3240 !important",
          "&:hover": {
            backgroundColor: "rgba(55,65,81,0.08)",
          },
        },
        "& .MuiTableSortLabel-icon": {
          color: "#2b3240 !important",
        },
        "& span > svg": {
          color: "#2b3240 !important",
        },
        "& .MuiInputAdornment-root svg": {
          color: "#2b3240 !important",
        },
      },
    },

    muiTableBodyCellProps: {
      sx: {
        fontSize: "14px",
        color: "#2b3240",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontWeight: "500",
        borderBottom: "1px solid #f0f0f0",
        padding: "16px 12px",
      },
    },

    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick?.(row.original),
      sx: {
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor: "rgba(189, 30, 44, 0.04) !important",
          transform: "translateY(-1px)",
          boxShadow: "0 2px 8px rgba(189, 30, 44, 0.1)",
        },
        "&.Mui-selected": {
          backgroundColor: "rgba(189, 30, 44, 0.08) !important",
        },
        "&.Mui-selected:hover": {
          backgroundColor: "rgba(189, 30, 44, 0.12) !important",
        },
      },
    }),

    muiToolbarAlertBannerProps: {
      sx: {
        backgroundColor: "rgba(189, 30, 44, 0.1)",
        color: "#BD1E2C",
        borderRadius: "8px",
        border: "1px solid rgba(189, 30, 44, 0.2)",
      },
    },

    muiSearchTextFieldProps: {
      sx: {
        "& .MuiInputBase-root": {
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          border: "1px solid #e1e5e9",
          color: "#2b3240",
          "& input": {
            color: "#2b3240",
            "&::placeholder": {
              color: "#6b7280",
            },
          },
          "&:hover": {
            borderColor: "#BD1E2C",
          },
          "&.Mui-focused": {
            borderColor: "#BD1E2C",
            boxShadow: "0 0 0 3px rgba(189, 30, 44, 0.1)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#BD1E2C !important",
          },
          "&.Mui-focused .MuiInputBase-input": {
            color: "#2b3240",
          },
        },
        "& .MuiInputBase-root .Mui-focused": {
          borderColor: "#BD1E2C",
        },
        "& .MuiInputBase-root .Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#BD1E2C",
        },
        "& .MuiInputBase-root .Mui-focused .MuiOutlinedInput-notchedOutline:hover":
          {
            borderColor: "#BD1E2C",
          },
        "& .MuiInputBase-root:focus-within": {
          borderColor: "#BD1E2C",
        },
        "& .MuiInputBase-root:focus-within .MuiOutlinedInput-notchedOutline": {
          borderColor: "#BD1E2C",
        },
      },
    },

    muiFilterTextFieldProps: {
      sx: {
        "& .MuiInputBase-root": {
          backgroundColor: "#ffffff",
          borderRadius: "6px",
          border: "1px solid #e1e5e9",
          fontSize: "13px",
          color: "#2b3240",
          "& input": {
            color: "#2b3240",
            "&::placeholder": {
              color: "#6b7280",
            },
          },
          "&:hover": {
            borderColor: "#BD1E2C",
          },
          "&.Mui-focused": {
            borderColor: "#BD1E2C",
            boxShadow: "0 0 0 2px rgba(189, 30, 44, 0.1)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#BD1E2C !important",
          },
          "&.Mui-focused .MuiInputBase-input": {
            color: "#2b3240",
          },
        },
        "& .MuiInputBase-root .Mui-focused": {
          borderColor: "#BD1E2C",
        },
        "& .MuiInputBase-root .Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#BD1E2C",
        },
        "& .MuiInputBase-root .Mui-focused .MuiOutlinedInput-notchedOutline:hover":
          {
            borderColor: "#BD1E2C",
          },
        "& .MuiInputBase-root:focus-within": {
          borderColor: "#BD1E2C",
        },
        "& .MuiInputBase-root:focus-within .MuiOutlinedInput-notchedOutline": {
          borderColor: "#BD1E2C",
        },
      },
    },

    muiTablePaginationProps: {
      labelDisplayedRows: ({ from, to, count }) =>
        `${from}–${to} di ${count !== -1 ? count : `più di ${to}`}`,
      sx: {
        "& .MuiTablePagination-selectLabel": {
          color: "#6b7280",
          fontWeight: "500",
        },
        "& .MuiTablePagination-displayedRows": {
          color: "#2b3240",
          fontWeight: "500",
        },
        "& .MuiIconButton-root": {
          color: "#6b7280",
          "&:hover": {
            backgroundColor: "rgba(189, 30, 44, 0.1)",
            color: "#BD1E2C",
          },
          "&.Mui-disabled": {
            color: "#d1d5db",
          },
        },
      },
    },

    muiTableHeadCellFilterModeMenuButtonProps: ({ column, table }) => {
      // Find the filter mode for this column
      const filter = table
        .getState()
        .columnFilters.find((f) => f.id === column.id);
      const filterFn = filter?.filterFn || filter?.operator;
      const isActive = filterFn && filterFn !== "equals";
      return {
        sx: {
          backgroundColor: isActive ? "#F87171" : "transparent", // light red if not equals
          color: isActive ? "#fff" : "#2b3240",
          borderRadius: "6px",
          transition: "background 0.2s",
          boxShadow: isActive ? "0 1px 4px rgba(248,113,113,0.15)" : "none",
          "&:hover": {
            backgroundColor: isActive ? "#F43F5E" : "#f0f0f0",
          },
        },
      };
    },
  };

  const table = useMaterialReactTable(tableConfig);

  return (
    <div className="table-container">
      <MaterialReactTable table={table} />
    </div>
  );
};

export default Table;
